/**
 * Shared cache for get911ServiceFees.
 *
 * The fee is fetched by two components — PlanCardFooter on the plans page and
 * OrderSummary on the pages that show the cart — each holding its own state. So
 * selecting a plan and landing on payment fetched the same plan and number
 * twice, once per component, with neither able to see the other's result.
 *
 * get911ServiceFees(pricePlan, phoneNumber) takes those two inputs and nothing
 * else, so the same pair can only return the same fee. Caching the in-flight
 * promise also collapses concurrent callers into one request.
 *
 * A response is only cached once it actually carries an entry: an empty result
 * means the fee is not available yet, and both callers retry in that case, which
 * a cached empty would defeat.
 */

const cache = new Map()

const hasFeeEntry = (response) => Array.isArray(response) && response.length > 0

export const fetchServiceFee911 = (get911ServiceFees, planId, phoneNumber) => {
  if (!planId || !phoneNumber) return Promise.resolve(null)

  const key = `${planId}|${phoneNumber}`
  const cached = cache.get(key)
  if (cached) return cached

  const request = Promise.resolve(get911ServiceFees(planId, phoneNumber))
    .then((response) => {
      // Keep only a usable answer, so a failed or empty lookup can be retried.
      if (!hasFeeEntry(response)) cache.delete(key)
      return response
    })
    .catch((error) => {
      cache.delete(key)
      throw error
    })

  cache.set(key, request)
  return request
}

export const resetServiceFee911Cache = () => cache.clear()
